import React, { useEffect, useState, useRef, useMemo } from 'react';
import useGameBase from '../../hooks/useGameBase';
import SpectatorPerspectiveSelector from "./SpectatorPerspectiveSelector";

// Basic placement editor helpers
function cellsForShip(s) {
  const cells = [];
  for (let i = 0; i < s.length; i++) {
    const cx = s.dir === 'H' ? s.x + i : s.x;
    const cy = s.dir === 'V' ? s.y + i : s.y;
    cells.push({ x: cx, y: cy });
  }
  return cells;
}

// helper to find grid cell coords from a pointer event
function getCellFromEvent(ev) {
  if (!ev || typeof ev.clientX !== 'number' || typeof ev.clientY !== 'number') return null;
  try {
    const el = document.elementFromPoint(ev.clientX, ev.clientY);
    if (!el) return null;
    const cellEl = el.closest && el.closest('[data-x][data-y]');
    if (!cellEl) return null;
    const x = parseInt(cellEl.dataset.x, 10);
    const y = parseInt(cellEl.dataset.y, 10);
    if (Number.isNaN(x) || Number.isNaN(y)) return null;
    return { x, y };
  } catch (e) {
    return null;
  }
}

// partial validation: checks shape, bounds and overlap but doesn't require full fleet count
function validatePartial(ships, gridSize = 10) {
  if (!Array.isArray(ships)) return 'Invalid ships';
  const occ = new Set();
  for (const s of ships) {
    if (typeof s.x !== 'number' || typeof s.y !== 'number' || typeof s.length !== 'number' || !['H','V'].includes(s.dir)) return 'Invalid ship data.';
    for (let i=0;i<s.length;i++){
      const cx = s.dir === 'H' ? s.x + i : s.x;
      const cy = s.dir === 'V' ? s.y + i : s.y;
      if (cx < 0 || cy < 0 || cx >= gridSize || cy >= gridSize) return 'A ship is out of bounds.';
      const key = `${cx},${cy}`;
      if (occ.has(key)) return 'Ships overlap.';
      occ.add(key);
    }
  }
  return null;
}

export default function SinkingShipsGame({ gameState, gamePlayers = [], user = {}, socket, roomId, isSpectator = false, onGameAction }) {
  // --- Spectator Perspective logic (modular, opt-in, matches Canasta) ---
  const perspectiveOptions = useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.players) && gameState.players.length > 0) return gameState.players;
    return [];
  }, [gamePlayers, gameState?.players]);

  const [spectatorViewPlayerId, setSpectatorViewPlayerId] = useState(null);
  const perspectivePlayer = useMemo(() => {
    if (!isSpectator || !spectatorViewPlayerId) return null;
    return perspectiveOptions.find((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    }) || null;
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  useEffect(() => {
    if (!isSpectator) {
      setSpectatorViewPlayerId(null);
      return;
    }
    if (!Array.isArray(perspectiveOptions) || perspectiveOptions.length === 0) {
      setSpectatorViewPlayerId(null);
      return;
    }
    const exists = perspectiveOptions.some((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    });
    if (!exists) {
      const first = perspectiveOptions[0];
      setSpectatorViewPlayerId(first?.userId || first?.socketId || first?.id || first?.username || null);
    }
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  const { submitAction, perspectiveIds } = useGameBase({ gameState, gamePlayers, user, socket, roomId, perspectivePlayer });
  const perspectiveIdSet = useMemo(
    () => new Set((perspectiveIds || []).filter(Boolean).map((value) => String(value))),
    [perspectiveIds]
  );
  const statePlayers = useMemo(() => (Array.isArray(gameState?.players) ? gameState.players : []), [gameState?.players]);
  const activePlayer = useMemo(() => {
    if (perspectiveIdSet.size === 0) return null;
    return statePlayers.find((player) => {
      const ids = [player?.socketId, player?.userId, player?.id, player?.username]
        .filter((value) => value !== undefined && value !== null)
        .map((value) => String(value));
      return ids.some((value) => perspectiveIdSet.has(value));
    }) || null;
  }, [statePlayers, perspectiveIdSet]);
  const activeSocketId = activePlayer?.socketId || perspectivePlayer?.socketId || user.socketId || null;
  const canInteract = !isSpectator && !!activeSocketId && String(activeSocketId) === String(user.socketId);
  const shotsByShooter = useMemo(() => gameState?.shots || {}, [gameState?.shots]);
  const gridSize = (gameState && gameState.gridSize) || 10;
  const shipsPerPlayer = (gameState && gameState.shipsPerPlayer) || 5;
  const currentPhase = gameState ? gameState.phase : null;

  const [localShips, setLocalShips] = useState(() => []);
  const [selectedLen, setSelectedLen] = useState(5);
  const [selectedDir, setSelectedDir] = useState('H');
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(null); // {type:'new'|'move', length, dir, startX, startY, shipIndex, anchorIndex}
  const [ghost, setGhost] = useState(null); // {x,y,length,dir,valid}
  const dragMovedRef = useRef(false);
  // animation state for misses
  // track last animated index for own shots (number) and per-opponent last index map
  const seenOwnLastIndexRef = useRef({}); // { perspectiveId: lastIndex }
  const seenOpponentLastIndexRef = useRef({}); // { perspectiveId: { shooterId: lastIndex } }
  const [finalMisses, setFinalMisses] = useState({}); // persistent final frame
  const [finalHits, setFinalHits] = useState({});
  // incoming opponent final state

  const [finalIncomingHits, setFinalIncomingHits] = useState({});
  const [finalIncomingMisses, setFinalIncomingMisses] = useState({});
  const [opponentShake, setOpponentShake] = useState(false);
  const [ownShake, setOwnShake] = useState(false);
  // track opponent sunk ship cells to render them gray
  const [sunkOpponentCells, setSunkOpponentCells] = useState({}); // { 'x,y': true }

  // autosave refs: remember last saved snapshot to avoid clobbering local edits
  const lastSavedRef = useRef(null); // snapshot of ships last sent to server
  const localDirtyRef = useRef(false); // true when localShips changed since last save
  const autosaveTimerRef = useRef(null);
  const ownShakeTimerRef = useRef(null);
  const opponentShakeTimerRef = useRef(null);

  const activeReady = !!(gameState?.playersReady && activeSocketId && gameState.playersReady[activeSocketId]);

  // Clear local placement and UI state when the game phase changes or a new round starts
  useEffect(() => {
    // When leaving positioning phase or when a new game/round begins, clear local-only state
    // This prevents localShips/ghost/dragging etc from persisting across games.
    // If phase is not positioning, clear placement UI state
    if (currentPhase !== 'positioning') {
      setLocalShips([]);
      setGhost(null);
      setDragging(null);
      setSelectedLen(5);
      setSelectedDir('H');
      setError(null);
      // clear autosave refs
      lastSavedRef.current = null;
      localDirtyRef.current = false;
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null; }
    }
    // Additionally, on any phase change we should clear transient animation state so a new
    // game doesn't show stale hit/miss animations from previous rounds.
    setFinalMisses({});
    setFinalHits({});
    setFinalIncomingHits({});
    setFinalIncomingMisses({});
    setSunkOpponentCells({});
    if (currentPhase === 'playing' && activeSocketId) {
      const myShots = shotsByShooter[activeSocketId] || [];
      seenOwnLastIndexRef.current = myShots.length ? myShots.length - 1 : -1;
      const seededOpponentIndices = {};
      for (const shooterId of Object.keys(shotsByShooter || {})) {
        if (shooterId === activeSocketId) continue;
        const shots = shotsByShooter[shooterId] || [];
        seededOpponentIndices[shooterId] = shots.length ? shots.length - 1 : -1;
      }
      seenOpponentLastIndexRef.current = seededOpponentIndices;
    } else {
      seenOpponentLastIndexRef.current = {};
      seenOwnLastIndexRef.current = -1;
    }
    // (analytics centralized in useGameBase)
    return () => {
      if (ownShakeTimerRef.current) {
        clearTimeout(ownShakeTimerRef.current);
        ownShakeTimerRef.current = null;
      }
      if (opponentShakeTimerRef.current) {
        clearTimeout(opponentShakeTimerRef.current);
        opponentShakeTimerRef.current = null;
      }
    };
  }, [currentPhase, activeSocketId, shotsByShooter]);

  const SHIP_COLORS = ['#9AD1FF', '#9BE6B8', '#D6B1FF', '#FFC7A6', '#FFE88A'];

  // Perspective-aware: sync positions for the selected perspective
  useEffect(() => {
    // sync positions from server if present
    const positions = (gameState && gameState.positions && activeSocketId && gameState.positions[activeSocketId]) || [];
    // If we have unsaved local edits, avoid overwriting them with server-broadcast positions.
    // Only accept server positions when localDirtyRef is false, or when server positions match lastSavedRef.
    try {
      const lastSaved = lastSavedRef.current;
      const serverJson = JSON.stringify(positions || []);
      const lastSavedJson = lastSaved ? JSON.stringify(lastSaved) : null;
      if (!localDirtyRef.current || (lastSavedJson && serverJson === lastSavedJson)) {
        setLocalShips(positions);
        localDirtyRef.current = false;
      } else {
        // keep local edits — do not clobber
      }
    } catch (e) {
      setLocalShips(positions);
      localDirtyRef.current = false;
    }
  }, [gameState, activeSocketId]);

  // autosave localShips to server when in positioning phase and not ready. Debounced to avoid flooding.
  useEffect(() => {
    // Do not autosave when not in positioning phase or when already marked ready
    if (!gameState || gameState.phase !== 'positioning' || !canInteract || !activeSocketId) return;
    if (activeReady) return;

    // mark dirty whenever localShips changes
    localDirtyRef.current = true;

    // clear any existing timer
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);

    // debounce save (600ms)
    autosaveTimerRef.current = setTimeout(async () => {
      // if localShips equals lastSaved, nothing to do
      try {
        const curJson = JSON.stringify(localShips || []);
        const lastJson = lastSavedRef.current ? JSON.stringify(lastSavedRef.current) : null;
        if (lastJson && curJson === lastJson) {
          localDirtyRef.current = false;
          return;
        }

        const action = { type: 'placeShips', ships: localShips, ready: false };
        let res;
        if (onGameAction) res = onGameAction(action); else res = await submitAction(action);
        // on success, update lastSaved and clear dirty flag
        if (!res || !res.error) {
          lastSavedRef.current = JSON.parse(JSON.stringify(localShips || []));
          localDirtyRef.current = false;
        }
      } catch (e) {
        // swallow - we'll try again on next change
      }
    }, 600);

    return () => {
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null; }
    };
  }, [localShips, gameState, canInteract, activeReady, activeSocketId, onGameAction, submitAction]);

  

  // Detect new opponent shots so we can animate incoming missiles onto the player's grid
  useEffect(() => {
    if (!activeSocketId) return;
    const perspectiveId = activeSocketId;
    try {
      const shotsMap = (gameState && gameState.shots) || {};
      for (const shooterId of Object.keys(shotsMap || {})) {
        if (shooterId === perspectiveId) continue;
        const shots = shotsMap[shooterId] || [];
        if (!shots || shots.length === 0) continue;
        const lastIndex = shots.length - 1;
        const prevIndex = (seenOpponentLastIndexRef.current?.[perspectiveId]?.[shooterId]) ?? -1;
        if (lastIndex <= prevIndex) continue;
        // update last-seen index for this perspective
        seenOpponentLastIndexRef.current = {
          ...seenOpponentLastIndexRef.current,
          [perspectiveId]: {
            ...(seenOpponentLastIndexRef.current?.[perspectiveId] || {}),
            [shooterId]: lastIndex
          }
        };
        const shot = shots[lastIndex];
        const k = `${shot.x},${shot.y}`;
        if (shot.hit) {
          // brief shake of own grid for direct hit (for this perspective)
          setOwnShake(false); // restart animation
          setTimeout(() => setOwnShake(true), 0);
          if (ownShakeTimerRef.current) clearTimeout(ownShakeTimerRef.current);
          ownShakeTimerRef.current = setTimeout(() => {
            setOwnShake(false);
            ownShakeTimerRef.current = null;
          }, 360);
          if (shot.sunk) {
            const ship = (localShips || []).find(sh => {
              for (let i = 0; i < sh.length; i++) {
                const cx = sh.dir === 'H' ? sh.x + i : sh.x;
                const cy = sh.dir === 'V' ? sh.y + i : sh.y;
                if (cx === shot.x && cy === shot.y) return true;
              }
              return false;
            });
            if (ship) {
              for (let i = 0; i < ship.length; i++) {
                const cx = ship.dir === 'H' ? ship.x + i : ship.x;
                const cy = ship.dir === 'V' ? ship.y + i : ship.y;
                const kk = `${cx},${cy}`;
                setFinalIncomingHits(prev => ({ ...prev, [kk]: true }));
              }
            }
          } else {
            setFinalIncomingHits(prev => ({ ...prev, [k]: true }));
          }
        } else {
          setFinalIncomingMisses(prev => ({ ...prev, [k]: true }));
        }
      }
    } catch (e) {
      // ignore
    }
  }, [gameState, activeSocketId, localShips]);

  // Detect new shots by this player and animate the most recent one only
  useEffect(() => {
    if (!activeSocketId) return;
    const perspectiveId = activeSocketId;
    try {
      // Only animate outgoing shots when it's this perspective's turn
      const amMyTurn = !!(gameState && gameState.turn === perspectiveId);
      if (!amMyTurn) return;
      const shotsMap = (gameState && gameState.shots) || {};
      const myShots = shotsMap[perspectiveId] || [];
      if (!myShots || myShots.length === 0) return;
      const lastIndex = myShots.length - 1;
      const prevIndex = seenOwnLastIndexRef.current?.[perspectiveId] ?? -1;
      if (lastIndex <= prevIndex) return;
      // mark newest as seen so we don't re-animate on subsequent updates
      seenOwnLastIndexRef.current = {
        ...seenOwnLastIndexRef.current,
        [perspectiveId]: lastIndex
      };
      const s = myShots[lastIndex];
      const k = `${s.x},${s.y}`;

      // perform same animation behavior as before for hits/misses
      if (s.hit) {
        // brief shake of opponent grid for direct hit (for this perspective)
        setOpponentShake(false); // restart animation
        setTimeout(() => setOpponentShake(true), 0);
        if (opponentShakeTimerRef.current) clearTimeout(opponentShakeTimerRef.current);
        opponentShakeTimerRef.current = setTimeout(() => {
          setOpponentShake(false);
          opponentShakeTimerRef.current = null;
        }, 360);
        if (s.sunk) {
          const playerIds = statePlayers.map((player) => player?.socketId).filter(Boolean);
          const target = playerIds.find((id) => id !== perspectiveId);
          const oppShips = (gameState && gameState.positions && gameState.positions[target]) || [];
          const ship = oppShips.find(sh => {
            for (let i=0;i<sh.length;i++){
              const cx = sh.dir === 'H' ? sh.x + i : sh.x;
              const cy = sh.dir === 'V' ? sh.y + i : sh.y;
              if (cx === s.x && cy === s.y) return true;
            }
            return false;
          });
          if (ship) {
            for (let i = 0; i < ship.length; i++) {
              const cx = ship.dir === 'H' ? ship.x + i : ship.x;
              const cy = ship.dir === 'V' ? ship.y + i : ship.y;
              const kk2 = `${cx},${cy}`;
              setSunkOpponentCells(prev => ({ ...prev, [kk2]: true }));
              setFinalHits(prev => ({ ...prev, [kk2]: true }));
            }
          }
        } else {
          setFinalHits(prev => ({ ...prev, [k]: true }));
        }
      } else {
        setFinalMisses(prev => ({ ...prev, [k]: true }));
      }
    } catch (e) {
      // ignore
    }
  }, [gameState, activeSocketId, statePlayers]);

  // When we enter the playing phase, mark existing shots as seen so we don't animate history
  useEffect(() => {
    if (currentPhase !== 'playing' || !activeSocketId) return;
    try {
      const shotsMap = (gameState && gameState.shots) || {};
      // Initialize last-seen indices so existing history won't animate
      const myShots = shotsMap[activeSocketId] || [];
      seenOwnLastIndexRef.current = (myShots && myShots.length) ? (myShots.length - 1) : -1;
      for (const shooterId of Object.keys(shotsMap || {})) {
        if (shooterId === activeSocketId) continue;
        const shots = shotsMap[shooterId] || [];
        seenOpponentLastIndexRef.current = Object.assign({}, seenOpponentLastIndexRef.current, { [shooterId]: (shots && shots.length) ? (shots.length - 1) : -1 });
      }
    } catch (e) {
      // ignore
    }
  }, [currentPhase, gameState, activeSocketId]);

  const placeShipAt = (x, y) => {
    if (!canInteract) return;
    // prevent local edits if this player is already marked ready on the server
    if (activeReady) return;
    const ship = { x, y, length: selectedLen, dir: selectedDir };
    setLocalShips(prev => {
      // if click on an existing ship cell, attempt to rotate that ship (validate)
      for (let i = 0; i < prev.length; i++) {
        const s = prev[i];
        const cells = cellsForShip(s);
        if (cells.some(c => c.x === x && c.y === y)) {
          const rotated = Object.assign({}, s, { dir: s.dir === 'H' ? 'V' : 'H' });
          const updated = [...prev];
          updated[i] = rotated;
          const v = validateLocal(updated);
          if (!v) return updated;
          setError(v || 'Cannot rotate here');
          return prev;
        }
      }
      // else add new ship (if count not exceeded)
      if (prev.length >= shipsPerPlayer) return prev;
      return prev.concat(ship);
    });
    setError(null);
  };

  const pointerDown = (ev) => {
    ev.preventDefault();
    dragMovedRef.current = false;
    if (!canInteract) return;
    // if player is ready, do not allow further dragging
    if (activeReady) return;
    const cell = getCellFromEvent(ev);
    if (!cell) return;
    const { x: cellX, y: cellY } = cell;
    // if pointer down on an existing ship cell -> start a move drag for that ship
    const idx = localShips.findIndex(s => cellsForShip(s).some(c => c.x === cellX && c.y === cellY));
    if (idx >= 0) {
      const s = localShips[idx];
      const cells = cellsForShip(s);
      const anchorIndex = cells.findIndex(c => c.x === cellX && c.y === cellY);
      setDragging({ type: 'move', shipIndex: idx, anchorIndex, length: s.length, dir: s.dir, startX: cellX, startY: cellY });
      const topLeft = { x: s.x, y: s.y, length: s.length, dir: s.dir };
      setGhost(Object.assign({}, topLeft, { valid: true }));
      try { ev.target.setPointerCapture(ev.pointerId); } catch (e) {}
      return;
    }
    // otherwise start a new-ship drag using selectedLen/selectedDir (only when not full fleet)
    if (localShips.length < shipsPerPlayer) {
      setDragging({ type: 'new', length: selectedLen, dir: selectedDir, startX: cellX, startY: cellY });
      setGhost({ x: cellX, y: cellY, length: selectedLen, dir: selectedDir, valid: true });
    }
    try { ev.target.setPointerCapture(ev.pointerId); } catch (e) {}
  };

  const pointerMove = (ev) => {
    if (!dragging) return;
    try { ev.preventDefault(); } catch(e) {}
    // mark that pointer has moved (so click handler can ignore clicks after drag)
    dragMovedRef.current = true;
    const cell = getCellFromEvent(ev);
    if (!cell) return;
    const { x: cellX, y: cellY } = cell;
    if (dragging.type === 'new') {
      const g = { x: cellX, y: cellY, length: dragging.length, dir: dragging.dir };
      const v = validatePartial([...(localShips || []), g]);
      setGhost(Object.assign({}, g, { valid: !v }));
      return;
    }
    // move existing ship: compute new top-left based on anchorIndex
    const s = localShips[dragging.shipIndex];
    if (!s) return;
    const anchor = dragging.anchorIndex || 0;
    let nx = cellX;
    let ny = cellY;
    if (dragging.dir === 'H') nx = cellX - anchor;
    else ny = cellY - anchor;
    const g = { x: nx, y: ny, length: dragging.length, dir: dragging.dir };
    // create provisional ships with moved ship
    const provisional = localShips.map((sh, i) => i === dragging.shipIndex ? Object.assign({}, sh, { x: g.x, y: g.y, dir: g.dir }) : sh);
    const v = validatePartial(provisional);
    setGhost(Object.assign({}, g, { valid: !v }));
  };

  const pointerUp = (ev) => {
    if (!dragging) return;
    try { ev.preventDefault(); } catch(e) {}
    const cell = getCellFromEvent(ev);
    if (!cell) {
      setDragging(null);
      setGhost(null);
      try { ev.target.releasePointerCapture(ev.pointerId); } catch (e) {}
      setTimeout(() => { dragMovedRef.current = false; }, 50);
      return;
    }
    // guard: if player became ready mid-drag, drop changes and exit
    if (!canInteract || activeReady) {
      setDragging(null);
      setGhost(null);
      try { ev.target.releasePointerCapture(ev.pointerId); } catch (e) {}
      setTimeout(() => { dragMovedRef.current = false; }, 50);
      return;
    }
    const { x: cellX, y: cellY } = cell;
    if (dragging.type === 'new') {
      const g = { x: cellX, y: cellY, length: dragging.length, dir: dragging.dir };
      const v = validateLocal([...(localShips || []), g]);
      if (!v) {
        setLocalShips(prev => prev.concat({ x: g.x, y: g.y, length: g.length, dir: g.dir }));
        setError(null);
      } else {
        setError(v);
      }
    } else if (dragging.type === 'move') {
      // compute new top-left
      const anchor = dragging.anchorIndex || 0;
      let nx = cellX;
      let ny = cellY;
      if (dragging.dir === 'H') nx = cellX - anchor;
      else ny = cellY - anchor;
      const moved = localShips.map((sh, i) => i === dragging.shipIndex ? Object.assign({}, sh, { x: nx, y: ny, dir: dragging.dir }) : sh);
      const v = validatePartial(moved);
      if (!v) {
        setLocalShips(moved);
        setError(null);
      } else {
        setError(v || 'Cannot move ship there');
      }
    }
    setDragging(null);
    setGhost(null);
    try { ev.target.releasePointerCapture(ev.pointerId); } catch (e) {}
    // slight delay before clearing dragMoved so click after pointerup still suppressed
    setTimeout(() => { dragMovedRef.current = false; }, 50);
  };

  const validateLocal = (ships) => {
    if (!Array.isArray(ships) || ships.length !== shipsPerPlayer) return `Place exactly ${shipsPerPlayer} ships.`;
    const occ = new Set();
    for (const s of ships) {
      if (typeof s.x !== 'number' || typeof s.y !== 'number' || typeof s.length !== 'number' || !['H','V'].includes(s.dir)) return 'Invalid ship data.';
      for (let i=0;i<s.length;i++){
        const cx = s.dir === 'H' ? s.x + i : s.x;
        const cy = s.dir === 'V' ? s.y + i : s.y;
        if (cx < 0 || cy < 0 || cx >= gridSize || cy >= gridSize) return 'A ship is out of bounds.';
        const key = `${cx},${cy}`;
        if (occ.has(key)) return 'Ships overlap.';
        occ.add(key);
      }
    }
    return null;
  };

  const submitReady = async () => {
    if (!canInteract) return;
    const v = validateLocal(localShips);
    if (v) { setError(v); return; }
    const action = { type: 'placeShips', ships: localShips, ready: true };
    let res;
    if (onGameAction) res = onGameAction(action); else res = await submitAction(action);
    if (res && res.error) setError(res.error);
  };

  const shoot = async (x, y) => {
    if (!canInteract) return;
    const action = { type: 'shoot', x, y };
    if (onGameAction) onGameAction(action); else await submitAction(action);
  };

  const grid = Array.from({ length: gridSize }).map((_, y) => Array.from({ length: gridSize }).map((_, x) => ({ x, y })));

  // Perspective-aware: is it this perspective's turn?
  const isMyTurn = !!(gameState && activeSocketId && gameState.turn === activeSocketId);

  // Perspective-aware: compute player/opponent ids for the selected perspective
  const playerIds = statePlayers.map((player) => player?.socketId).filter(Boolean);
  const opponentIds = playerIds.filter((id) => id !== activeSocketId);
  const opponentShots = [];
  try {
    for (const oid of opponentIds) {
      const s = (gameState && gameState.shots && gameState.shots[oid]) || [];
      for (const shot of s) opponentShots.push(Object.assign({ from: oid }, shot));
    }
  } catch (e) {
    // ignore
  }
  const opponentShotsMap = Object.fromEntries((opponentShots || []).map(s => [`${s.x},${s.y}`, s]));
  const leaderboardPlayers = statePlayers.length > 0 ? statePlayers : gamePlayers;
  const winnerIds = Array.isArray(gameState?.result?.winners)
    ? gameState.result.winners
    : Array.isArray(gameState?.winners)
      ? gameState.winners
      : gameState?.winnerId
        ? [gameState.winnerId]
        : [];
  const shotCounts = useMemo(() => {
    if (gameState?.result?.breakdown?.shotCounts) {
      return gameState.result.breakdown.shotCounts;
    }
    if (gameState?.shotCounts) {
      return gameState.shotCounts;
    }
    return Object.fromEntries(
      (leaderboardPlayers || []).map((player) => [
        player.socketId,
        Array.isArray(gameState?.shots?.[player.socketId]) ? gameState.shots[player.socketId].length : 0,
      ])
    );
  }, [gameState?.result?.breakdown?.shotCounts, gameState?.shotCounts, gameState?.shots, leaderboardPlayers]);
  const finalLeaderboard = useMemo(() => {
    return (leaderboardPlayers || [])
      .map((player) => ({
        player,
        shotCount: Number(shotCounts?.[player.socketId] || 0),
        isWinner: winnerIds.includes(player.socketId),
      }))
      .sort((left, right) => {
        if (left.shotCount !== right.shotCount) {
          return left.shotCount - right.shotCount;
        }
        return String(left.player?.username || '').localeCompare(String(right.player?.username || ''));
      });
  }, [leaderboardPlayers, shotCounts, winnerIds]);
  const winnerDisplayName = useMemo(() => {
    const winnerId = winnerIds[0];
    if (!winnerId) return 'None';
    return leaderboardPlayers.find((player) => player.socketId === winnerId)?.username || winnerId;
  }, [leaderboardPlayers, winnerIds]);

  return (
    <div className="sinkingships-game p-4 text-gray-900 dark:text-gray-100">
      {/* Spectator Perspective Selector (if spectator) */}
      {isSpectator && (
        <SpectatorPerspectiveSelector
          players={perspectiveOptions}
          value={spectatorViewPlayerId}
          onChange={setSpectatorViewPlayerId}
          className="mb-4"
        />
      )}
      {/* local CSS for splash animation */}
      <style>{`
        @keyframes sinking-splash {
          0% { transform: scale(0.6); opacity: 0.9; box-shadow: 0 0 0 0 rgba(3,169,244,0.6); }
          60% { transform: scale(1.1); opacity: 0.6; box-shadow: 0 0 0 10px rgba(3,169,244,0.0); }
          100% { transform: scale(1); opacity: 1; box-shadow: inset 0 0 0 6px rgba(3,90,140,0.9); }
        }
        .splash-anim {
          width: 18px; height: 18px; border-radius: 50%; display:inline-block;
          background: rgba(3,169,244,0.6);
        }
        .splash-final {
          width: 12px; height: 12px; border-radius: 50%; display:inline-block; background: rgba(3,90,140,0.95);
          box-shadow: 0 0 4px rgba(0,0,0,0.25);
        }
        @keyframes explosion {
          0% { transform: scale(0.4); opacity: 0.95; filter: hue-rotate(0deg); }
          50% { transform: scale(1.4); opacity: 0.9; filter: hue-rotate(20deg); }
          100% { transform: scale(1); opacity: 1; }
        }
        .explosion-anim {
          width: 20px; height: 20px; border-radius: 50%; display:inline-block; background: radial-gradient(circle at 40% 35%, #fff 0%, #ffcc33 20%, #ff5722 60%, #a33 100%);
          box-shadow: 0 0 8px rgba(255,80,0,0.6);
        }
        .hit-final {
          width: 10px; height: 10px; border-radius: 50%; display:inline-block; background: rgba(200,20,20,0.95);
          box-shadow: 0 0 6px rgba(160,0,0,0.9);
        }
        /* 3D perspective styling for player's own grid */
        .own-grid-3d {
          width: 330px; margin: 12px auto 0; perspective: 800px; -webkit-perspective: 800px;
        }
        .own-grid-inner {
          transform-origin: 50% 0%;
          transform: rotateX(20deg) translateY(6px) scale(1);
          xbox-shadow: 0 8px 20px rgba(0,0,0,0.25);
          border-radius: 6px; overflow: hidden; background: rgba(255,255,255,0.03);
        }
        /* incoming missile animation */
        @keyframes missile-fly {
          0% { transform: translate3d(var(--mx,0), var(--my,0), 0) scale(1); opacity: 0.95; }
          60% { opacity: 0.9; }
          100% { transform: translate3d(0,0,0) scale(0.3); opacity: 1; }
        }
        .incoming-missile {
          position: absolute; width: 48px; height: 48px; border-radius: 50%; background: rgba(120,120,120,0.95); z-index: 50; box-shadow: 0 6px 18px rgba(0,0,0,0.45); transform-origin: 50% 50%;
        }
        /* smoke puff on impact */
        @keyframes smoke-puff {
          0% { transform: translateY(0) scale(0.35); opacity: 0.95; filter: blur(0px); }
          50% { transform: translateY(-6px) scale(0.95); opacity: 0.82; filter: blur(0.8px); }
          100% { transform: translateY(-12px) scale(1.25); opacity: 0; filter: blur(1.2px); }
        }
        .smoke-burst {
          position: absolute; width: 28px; height: 18px; left: 48%; top: 48%; transform: translate(-50%,-50%) translateZ(0); pointer-events: none; z-index: 60; opacity: 0.95;
          background: radial-gradient(closest-side, rgba(120,120,120,0.95) 0%, rgba(160,160,160,0.85) 40%, rgba(200,200,200,0.6) 100%);
          border-radius: 20px; animation: smoke-puff 600ms ease-out forwards;
        }
        /* short camera/grid shake for hits */
        @keyframes grid-shake {
          0% { transform: translate3d(0,0,0); }
          20% { transform: translate3d(-6px,2px,0) rotate(-0.8deg); }
          40% { transform: translate3d(6px,-2px,0) rotate(0.6deg); }
          60% { transform: translate3d(-4px,1px,0) rotate(-0.4deg); }
          80% { transform: translate3d(2px,-1px,0) rotate(0.2deg); }
          100% { transform: translate3d(0,0,0) rotate(0deg); }
        }
        /* pulsating gray target for selectable opponent cells */
        .target-symbol {
          width: 24px; height: 24px; display: inline-block; pointer-events: none; transform-origin: center center;
          transition: transform 120ms ease, opacity 120ms ease, color 120ms ease;
          /* make the icon more faint by default */
          color: rgba(156, 163, 175, 0.23); /* faint gray */
        }
        /* selectable opponent cell hover effect */
        .opp-cell.selectable:hover {
          background: rgba(156,163,175,0.08); /* subtle gray highlight */
        }
        .opp-cell.selectable:hover .target-symbol {
          transform: scale(1.12);
          opacity: 0.98;
          /* Slightly darker on hover in light mode */
          color: rgba(31,41,55,0.85); /* slate-800 */
        }
        /* In dark mode the hover color should be lighter so it remains visible against dark backgrounds */
        .dark .opp-cell.selectable:hover .target-symbol {
          color: rgba(229,231,235,0.92); /* gray-200, fairly visible in dark mode */
        }
        .grid-shake {
          animation: grid-shake 360ms ease-out both;
        }
        /* utility to disable text-selection and touch gestures during drag */
        .noselect {
          -webkit-user-select: none;
          -ms-user-select: none;
          user-select: none;
          -webkit-touch-callout: none;
          touch-action: none;
          -ms-touch-action: none;
        }
      `}</style>
      

      {gameState.phase === 'positioning' && (
        <div className="flex flex-col md:flex-row gap-4">
          {/* Left: selection palette (if applicable) */}
          {localShips.length < shipsPerPlayer && (
            <div className="md:w-48">
              <div className="mb-2">Ship lengths</div>
              <div className="flex gap-2 mb-2">
                {[5,4,3,3,2].map(len => (
                  <button key={len} onClick={() => setSelectedLen(len)} disabled={!canInteract || activeReady} className={`px-2 py-1 border ${selectedLen===len?'bg-blue-600 text-white':''}`}>{len}</button>
                ))}
              </div>
              <div className="mb-2">Direction: <button onClick={() => setSelectedDir(d=>d==='H'?'V':'H')} disabled={!canInteract || activeReady} className="px-2 py-1 border">{selectedDir}</button></div>
              <div className="mb-2">Click an empty cell to place selected ship. Click a ship cell to rotate that ship. Drag a ship to move it.</div>
            </div>
          )}

          {/* Grid + controls: grid is responsive square, controls sit underneath */}
          <div className="flex-1">
            <div style={{ width: '330px', height: '330px', touchAction: 'none', WebkitUserSelect: 'none', userSelect: 'none' }} className="mx-auto noselect">
              <div style={{ display: 'grid', width: '100%', height: '100%', gridTemplateColumns: `repeat(${gridSize}, 1fr)`, gridTemplateRows: `repeat(${gridSize}, 1fr)`, gap: '0px' }}>
                {grid.map((row, y) => row.map(cell => {
                  const shipIndex = localShips.findIndex((s) => cellsForShip(s).some(c => c.x === cell.x && c.y === cell.y));
                  const isGhost = ghost && cellsForShip(ghost).some(c => c.x === cell.x && c.y === cell.y);
                  const bgStyle = {};
                  if (shipIndex >= 0) {
                    bgStyle.backgroundColor = SHIP_COLORS[shipIndex % SHIP_COLORS.length];
                    bgStyle.color = '#fff';
                  }
                  let ghostOverlay = null;
                  if (isGhost) ghostOverlay = ghost.valid ? 'rgba(192, 255, 209, 0.6)' : 'rgba(255, 161, 161, 0.6)';
                  const cursorStyle = dragging ? 'grabbing' : (shipIndex >= 0 ? 'grab' : 'default');
                  return (
                    <div key={`${cell.x}-${cell.y}`} data-x={cell.x} data-y={cell.y} onClick={() => { if (!dragMovedRef.current) placeShipAt(cell.x, cell.y); }} onPointerDown={(e)=>pointerDown(e)} onPointerMove={(e)=>pointerMove(e)} onPointerUp={(e)=>pointerUp(e)} onContextMenu={(e)=>{try{e.preventDefault();}catch(e){} }} style={Object.assign({ width: '100%', height: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', touchAction: 'none', WebkitUserSelect: 'none', userSelect: 'none' }, bgStyle, ghostOverlay ? { boxShadow: `inset 0 0 0 9999px ${ghostOverlay}` } : {}, { cursor: cursorStyle, border: '1px solid rgba(156,163,175,1)', backgroundColor: bgStyle.backgroundColor || (document && document.documentElement && document.documentElement.classList && document.documentElement.classList.contains('dark') ? '#111827' : '#f3f4f6') })}>
                      {shipIndex >= 0 ? '' : (isGhost ? '' : '')}
                    </div>
                  );
                }))}
              </div>
            </div>

            {/* Controls under the grid */}
            <div className="mt-3 text-center">
              <div className="mb-2 text-red-600">{error}</div>
              <div className="mb-1">Players ready:</div>
              <div className="flex gap-2 justify-center mb-2">
                {(gamePlayers || []).map(p => (
                  <div key={p.socketId} className={`px-2 py-1 border ${gameState.playersReady && gameState.playersReady[p.socketId] ? 'bg-green-500 text-white' : ''}`}>{p.username || p.socketId}</div>
                ))}
              </div>
              <div>
                <button onClick={submitReady} disabled={!canInteract || activeReady} className={`px-3 py-1 ${!canInteract || activeReady ? 'bg-gray-400' : 'bg-green-600 text-white'} rounded`}>I'm ready</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {gameState.phase === 'playing' && (
        <div>
          <div className="mb-3 text-center">Opponent Grid</div>
          <div style={{ width: '330px', height: '330px', touchAction: 'none', WebkitUserSelect: 'none', userSelect: 'none' }} className={`mx-auto ${opponentShake ? 'grid-shake' : ''} noselect`}>
            <div style={{ display: 'grid', width: '100%', height: '100%', gridTemplateColumns: `repeat(${gridSize}, 1fr)`, gridTemplateRows: `repeat(${gridSize}, 1fr)`, gap: '2px' }}>
                {grid.map((row, y) => row.map(cell => {
                const k = `${cell.x},${cell.y}`;
                const shotsMap = (gameState && gameState.shots) || {};
                const myShots = shotsMap[activeSocketId] || [];
                const shot = myShots.find(s => s.x === cell.x && s.y === cell.y);
                const isHitFinal = !!finalHits[k] || (!!shot && shot.hit === true);
                const isMissFinal = !!finalMisses[k] || (!!shot && shot.hit === false);
                const sunkCell = !!sunkOpponentCells[k];
                const oppCellStyle = Object.assign({ position: 'relative', touchAction: 'none', WebkitUserSelect: 'none', userSelect: 'none', width: '100%', height: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(148,163,184,0.5)' }, sunkCell ? { backgroundColor: '#9ca3af', color: '#fff' } : { backgroundColor: 'transparent' });
                return (
                  <button
                    key={`${cell.x}-${cell.y}`}
                    className={`opp-cell ${canInteract && isMyTurn && !isHitFinal && !isMissFinal && !shot ? 'selectable' : ''}`}
                    onClick={() => { if (canInteract && isMyTurn && !shot && !isHitFinal && !isMissFinal) shoot(cell.x, cell.y); }}
                    disabled={!canInteract || !isMyTurn || !!shot || isHitFinal || isMissFinal}
                    onContextMenu={(e)=>{try{e.preventDefault();}catch(e){}}}
                    style={oppCellStyle}
                  >
                    {isHitFinal ? <span className="hit-final" /> : (isMissFinal ? <span className="splash-final" /> : null)}
                    {/* show static gray target for selectable cells when it's this player's turn */}
                    {canInteract && isMyTurn && !isHitFinal && !isMissFinal && !shot && (
                      <svg className="target-symbol" viewBox="0 0 24 24" aria-hidden="true" style={{pointerEvents: 'none'}}>
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.2" fill="none" />
                        <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="1.0" fill="none" />
                        <line x1="12" y1="2" x2="12" y2="6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                        <line x1="12" y1="18" x2="12" y2="22" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                        <line x1="2" y1="12" x2="6" y2="12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                        <line x1="18" y1="12" x2="22" y2="12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                        <circle cx="12" cy="12" r="1.4" fill="currentColor" />
                      </svg>
                    )}
                    {sunkCell ? <span style={{ position: 'absolute', fontSize: '12px' }}></span> : null}
                  </button>
                );
              }))}
            </div>
          </div>
          {/* Player's own grid, with perspective to appear 3D */}
          
          <div className={`own-grid-3d ${ownShake ? 'grid-shake' : ''} noselect`} style={{ touchAction: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}>
            <div className={`own-grid-inner`} style={{ width: '330px', height: '180px' }}>
              <div style={{ display: 'grid', width: '100%', height: '100%', gridTemplateColumns: `repeat(${gridSize}, 1fr)`, gridTemplateRows: `repeat(${gridSize}, 1fr)`, gap: '2px', transform: 'scaleY(0.9) translateY(0%)' }}>
                {grid.map((row, y) => row.map(cell => {
                  const k = `${cell.x},${cell.y}`;
                  // determine if this cell is part of a local ship
                  const shipIndex = localShips.findIndex((s) => cellsForShip(s).some(c => c.x === cell.x && c.y === cell.y));
                  // check if hit by opponent
                  const oppShot = opponentShotsMap[k];
                  const isHit = !!(shipIndex >= 0 && (() => {
                    const ship = localShips[shipIndex];
                    const idx = ship.dir === 'H' ? (cell.x - ship.x) : (cell.y - ship.y);
                    return (ship.hitIndexes || []).includes(idx);
                  })());
                  const isMiss = !!(oppShot && oppShot.hit === false);
                  const bgStyle = {};
                  if (shipIndex >= 0) {
                    bgStyle.backgroundColor = SHIP_COLORS[shipIndex % SHIP_COLORS.length];
                    bgStyle.color = '#fff';
                  }
                  const finalHit = finalIncomingHits[k] || isHit;
                  const finalMiss = finalIncomingMisses[k] || isMiss;
                  const cellStyle = Object.assign({ width: '100%', height: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', border: '1px solid rgba(148,163,184,0.5)', position: 'relative', overflow: 'visible', touchAction: 'none', WebkitUserSelect: 'none', userSelect: 'none' }, bgStyle);
                  return (
                    <div key={`own-${cell.x}-${cell.y}`} onContextMenu={(e)=>{try{e.preventDefault();}catch(e){}}} style={cellStyle}>
                      {finalHit ? <span className="hit-final" /> : (finalMiss ? <span className="splash-final" /> : (shipIndex >= 0 ? '' : null))}
                    </div>
                  );
                }))}
              </div>
            </div>
          </div>
        <div className="mt-3 text-center">Your Grid</div>
        </div>
      )}

      {gameState.phase === 'finished' && (
        <div className="mx-auto mt-6 max-w-2xl rounded-2xl border border-sky-200 bg-white/90 p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
          <div className="text-center">
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-600 dark:text-sky-300">Shot Leaderboard</div>
            <div className="mt-2 text-2xl font-black text-slate-900 dark:text-white">Winner: {winnerDisplayName}</div>
            <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">Lower shot counts rank higher and match the high score metric recorded for the winner.</div>
          </div>

          <div className="mt-4 space-y-3">
            {finalLeaderboard.map(({ player, shotCount, isWinner }, index) => (
              <div
                key={player.socketId}
                className={`flex items-center justify-between rounded-2xl border px-4 py-3 ${
                  isWinner
                    ? 'border-amber-300 bg-amber-100 dark:border-amber-600 dark:bg-amber-950/40'
                    : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-sky-600 text-sm font-black text-white">
                    {index + 1}
                  </span>
                  <div>
                    <div className="font-bold text-slate-900 dark:text-white">{player.username || player.socketId}</div>
                    <div className="text-xs uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">{isWinner ? 'Recorded Winner Score' : 'Final Shot Count'}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-black text-slate-900 dark:text-white">{shotCount}</div>
                  <div className="text-xs uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">Shots</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


SinkingShipsGame.Guide = function SinkingShipsGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Sinking Ships</h3>
      <ul className="list-disc ml-6">
        <li>Each player takes turns placing their ships on the grid.</li>
        <li>Ships can be placed horizontally or vertically and cannot overlap.</li>
        <li>Use the controls to select ship length and direction, then click or drag on the grid to place or move ships.</li>
        <li>Once all players are ready, the game begins!</li>
        <li>Once all ships are placed, players take turns shooting at each other's grids.</li>
        <li>Hit a ship to mark it, sink all ships to win!</li>
      </ul>
    </div>
  );
};

